import type {AgentMergeEnrollmentAttempt} from '../page-data/payloads/agent-merge'

export type AgentMergeEnrollmentDisplayState = 'pending' | 'failed' | 'unconfirmed'

/**
 * Added to a `succeeded` attempt's own deadline before it is treated as unconfirmed. The backend
 * can report success slightly before the authoritative configuration is readable, so a short
 * grace period avoids flashing an "unconfirmed" banner for an enrollment that is about to
 * resolve normally on the very next poll.
 */
export const AGENT_MERGE_ENROLLMENT_SUCCESS_GRACE_MS = 15_000

/**
 * Bounded poll interval while an attempt is unresolved. Kept comfortably above the backend's
 * enrollment RPC timeout (9s) so a single slow attempt cannot cause overlapping polls.
 */
export const AGENT_MERGE_ENROLLMENT_POLL_INTERVAL_MS = 10_000

/**
 * The moment an attempt stops being eligible for display as `pending`/polling, in epoch
 * milliseconds. An unparsable deadline fails closed to "already expired" rather than polling
 * forever.
 */
export function agentMergeEnrollmentDeadlineMillis(attempt: AgentMergeEnrollmentAttempt): number {
  const deadline = Date.parse(attempt.deadlineAt)
  if (Number.isNaN(deadline)) return -Infinity
  return attempt.state === 'succeeded' ? deadline + AGENT_MERGE_ENROLLMENT_SUCCESS_GRACE_MS : deadline
}

/**
 * Derives what the section should show for a background enrollment attempt, independent of the
 * authoritative `enabled`/`allowedActions` fields the backend sends alongside it. Returns `null`
 * when normal controls should render untouched: there is no attempt, or it was `skipped` (which
 * is indistinguishable from no attempt ever having happened).
 *
 * `succeeded` is deliberately never surfaced as a distinct display state: until the authoritative
 * configuration itself arrives (at which point the backend omits this field entirely), a
 * `succeeded` attempt renders identically to `pending` so the UI never claims Agent Merge is on
 * based on this field alone.
 */
export function agentMergeEnrollmentDisplayState(
  attempt: AgentMergeEnrollmentAttempt | undefined,
  now: number,
): AgentMergeEnrollmentDisplayState | null {
  if (!attempt || attempt.state === 'skipped') return null
  if (attempt.state === 'failed') return 'failed'
  if (attempt.state === 'unconfirmed') return 'unconfirmed'

  // pending or succeeded
  return now > agentMergeEnrollmentDeadlineMillis(attempt) ? 'unconfirmed' : 'pending'
}

/**
 * Whether the configuration query should keep polling for this attempt. Bounded by the same
 * deadline (plus grace for `succeeded`) used for display, so polling and the visible placeholder
 * always stop together.
 */
export function shouldPollAgentMergeEnrollment(attempt: AgentMergeEnrollmentAttempt | undefined, now: number): boolean {
  if (!attempt || attempt.state === 'failed' || attempt.state === 'skipped') return false
  return now <= agentMergeEnrollmentDeadlineMillis(attempt)
}
